import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { getMyOrders } from '../services/pharmacyApi';

// ---------------------------------------------------------------------------
// Status badge — the backend currently only ever sets "PLACED" on creation
// (no admin flow yet moves an order to SHIPPED/DELIVERED/CANCELLED), but this
// is written to render whatever status string comes back so it doesn't need
// touching again once that flow exists.
// ---------------------------------------------------------------------------
const STATUS_META = {
  PLACED:    { label: 'Placed',    color: '#4f46e5', bg: '#eef2ff', border: '#c7d2fe' },
  CONFIRMED: { label: 'Confirmed', color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc' },
  SHIPPED:   { label: 'Shipped',   color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
  DELIVERED: { label: 'Delivered', color: '#059669', bg: '#ecfdf5', border: '#6ee7b7' },
  CANCELLED: { label: 'Cancelled', color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || { label: status || 'Unknown', color: '#6b7280', bg: '#f3f4f6', border: '#e5e7eb' };
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 9999,
      color: meta.color, background: meta.bg, border: `1px solid ${meta.border}`,
      fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap',
    }}>
      {meta.label}
    </span>
  );
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const SkeletonCard = () => (
  <div style={{
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16,
    padding: '18px 22px', marginBottom: 14,
  }}>
    <div className="skeleton skeleton-line" style={{ width: '35%', height: 13, marginBottom: 10 }} />
    <div className="skeleton skeleton-line" style={{ width: '55%', height: 11, marginBottom: 14 }} />
    <div className="skeleton skeleton-line" style={{ width: '25%', height: 13 }} />
  </div>
);

const OrderCard = ({ order }) => {
  const [expanded, setExpanded] = useState(false);
  const itemCount = order.items?.length || 0;

  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16,
      padding: '18px 22px', marginBottom: 14, boxShadow: '0 2px 12px rgba(79,70,229,0.05)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1e1b4b', fontFamily: 'DM Sans, sans-serif' }}>
            Order #{order.id?.slice(0, 8)}
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
            {formatDate(order.createdAt)} · {itemCount} item{itemCount !== 1 ? 's' : ''}
          </div>
        </div>
        <StatusBadge status={order.status} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1e1b4b' }}>
          ₹{Number(order.totalAmount ?? 0).toFixed(2)}
        </div>
        <button
          onClick={() => setExpanded(v => !v)}
          className="t-link"
          style={{ fontSize: 12, fontWeight: 600 }}
        >
          {expanded ? 'Hide details' : 'View details'}
        </button>
      </div>

      {expanded && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #f3f4f6' }}>
          {order.items?.map((item, i) => (
            <div key={item.productId || i} style={{
              display: 'flex', justifyContent: 'space-between', padding: '6px 0',
              fontSize: 12.5, color: '#374151',
            }}>
              <span>{item.productName} × {item.quantity}</span>
              <span style={{ color: '#6b7280' }}>₹{Number(item.totalPrice ?? 0).toFixed(2)}</span>
            </div>
          ))}
          {order.deliveryAddress && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#9ca3af' }}>
              Delivering to: {order.deliveryAddress}
            </div>
          )}
          {order.paymentMode && (
            <div style={{ marginTop: 2, fontSize: 12, color: '#9ca3af' }}>
              Payment: {order.paymentMode}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MyOrders = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { tenant } = useTenant();

  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!user) { navigate('/login', { replace: true }); return; }
  }, [user, navigate]);

  useEffect(() => {
    if (!token || !tenant?.id) return;
    setLoading(true);
    setError(null);
    getMyOrders(token, tenant.id)
      .then(setOrders)
      .catch(e => setError(e.message || 'Failed to load your orders.'))
      .finally(() => setLoading(false));
  }, [token, tenant?.id]);

  if (!user) return null;

  return (
    <div className="page">
      <main className="page-main" style={{ maxWidth: 640, margin: '0 auto' }}>

        <div className="breadcrumb" style={{ marginBottom: 16 }}>
          <button className="t-link" onClick={() => navigate('/')}>Home</button>
          <span>›</span>
          <button className="t-link" onClick={() => navigate('/services/pharmacy')}>Pharmacy</button>
          <span>›</span>
          <span className="bc-current">My orders</span>
        </div>

        <h1 className="t-h1" style={{ marginBottom: 4 }}>My orders</h1>
        <p className="t-body-sm" style={{ color: '#6b7280', marginBottom: 24 }}>
          Track the status of your pharmacy orders.
        </p>

        {loading && (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        )}

        {!loading && error && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 12,
            padding: '14px 18px', color: '#a32d2d', fontSize: 13,
          }}>
            {error}
          </div>
        )}

        {!loading && !error && orders.length === 0 && (
          <div style={{
            textAlign: 'center', padding: '48px 20px', color: '#9ca3af',
          }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>📦</div>
            <p style={{ fontSize: 14, marginBottom: 16 }}>You haven't placed any orders yet.</p>
            <button className="btn-primary btn-auto" onClick={() => navigate('/services/pharmacy')}>
              Browse pharmacy
            </button>
          </div>
        )}

        {!loading && !error && orders.map(order => (
          <OrderCard key={order.id} order={order} />
        ))}
      </main>
    </div>
  );
};

export default MyOrders;
